<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { ref } from 'vue';

import Checkbox from '@/components/base/form/Checkbox.vue';
import Modal from '@/components/base/layout/Modal.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header3 from '@/components/base/typography/Header3.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';

const props = defineProps<{ modelValue: boolean; supportMobile?: boolean }>();

const emit = defineEmits(['update:modelValue']);

const markedAsGift = useVModel(props, 'modelValue', emit);

const isModalOpen = ref(false);

const markAsGiftOnModal = () => {
  markedAsGift.value = !markedAsGift.value;
  isModalOpen.value = false;
};
</script>

<template>
  <div data-promo="1" data-promo-creative="pdp" data-promo-name="Mark as Gift">
    <div class="flex flex-row" :class="supportMobile ? 'text-xs lg:text-sm' : 'text-sm'">
      <Checkbox v-model="markedAsGift" class="mr-2 font-normal text-black">
        <p :class="{ 'ml-1': !supportMobile }">This is a gift</p>
      </Checkbox>
      <UnstyledButton
        class="mt-0 underline text-cyan-800 font-proxima-nova"
        :class="supportMobile ? 'text-xs lg:text-sm' : 'text-sm'"
        data-test="learn-more"
        data-promo="1"
        data-promo-creative="Learn more: This is a gift"
        data-promo-name="Infographic"
        @click="isModalOpen = true"
      >
        Learn more
      </UnstyledButton>
    </div>
    <Modal
      anchor="right"
      :hasControls="false"
      :isOpen="isModalOpen"
      skipFirstElementOnFocus
      width="md:max-w-lg"
      @handle-close="isModalOpen = false"
      data-test="gift-modal"
    >
      <template #header>
        <Header3 headerTag="span">Gift Order</Header3>
      </template>

      <template #body>
        <LargeBodyText class="mt-1 font-semibold text-neutral-500 md:mt-3 font-sofia-pro">
          Selecting "This is a gift" allows you to:
        </LargeBodyText>
        <div class="flex mt-4 md:mt-7">
          <img
            aria-hidden="true"
            alt="gift card with hidden price"
            class="self-center flex-shrink-0 object-contain w-16 h-16 mr-4 sm:w-24 sm:h-24"
            src="@/assets/pdp/gift-modal/hide-price.jpg"
          />
          <div class="self-center mt-1">
            <LargeBodyText class="font-bold leading-5 text-black">Hide the price</LargeBodyText>
            <BaseBodyText class="mt-1 text-neutral-500">
              They won't see the cost on the packing slip.
            </BaseBodyText>
          </div>
        </div>
        <div class="flex mt-4 md:mt-7">
          <img
            aria-hidden="true"
            alt="gift card with message"
            class="self-center flex-shrink-0 object-contain w-16 h-16 mr-4 sm:w-24 sm:h-24"
            src="@/assets/pdp/gift-modal/free-message.jpg"
          />
          <div class="self-center mt-3">
            <LargeBodyText class="font-bold leading-5 text-black">
              FREE gift message
            </LargeBodyText>
            <BaseBodyText class="mt-1 text-neutral-500">
              You can add a personalized gift message at checkout.
            </BaseBodyText>
          </div>
        </div>
        <div class="flex mt-4 md:mt-7">
          <img
            aria-hidden="true"
            alt="calendar"
            class="self-center flex-shrink-0 object-contain w-16 h-16 mr-4 sm:w-24 sm:h-24"
            src="@/assets/pdp/gift-modal/calendar.jpg"
          />
          <div class="self-center mt-1">
            <LargeBodyText class="font-semibold leading-5 text-black">
              Ship it later!
            </LargeBodyText>
            <BaseBodyText class="mt-1 text-neutral-500">
              Schedule your shipment for a later date at checkout.
            </BaseBodyText>
          </div>
        </div>
        <div class="sm:hidden">
          <LargeBodyText class="mt-6 font-semibold text-neutral-500 md:mt-10 font-sofia-pro">
            Make this item a gift:
          </LargeBodyText>
          <Checkbox
            v-model="markedAsGift"
            class="mt-3 mr-2 font-normal text-black"
            @click="markAsGiftOnModal"
          >
            <p :class="{ 'ml-1': !supportMobile }">This is a gift</p>
          </Checkbox>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
p {
  @apply mb-0;
}
</style>
